import { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import {
  useAdminSuggestions,
  useAdminUpdateSuggestion,
  useAdminDeleteSuggestion,
  useIdeaBoardProfiles,
  useIdeaBoardVotes,
  useIdeaBoardCategoryNames,
  useIdeaBoardAttachments,
} from '@/idea-board/hooks/useIdeaBoardData'
import IdeaAttachmentPanel from '@/idea-board/components/IdeaAttachmentPanel'
import { ideaBoardDisplayName } from '@/idea-board/lib/ideaBoardUser'
import CategoryBadge from '@/idea-board/components/CategoryBadge'
import CategoryAdminPanel from '@/idea-board/components/CategoryAdminPanel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Shield,
  ThumbsUp,
  ThumbsDown,
  Eye,
  EyeOff,
  Users,
  ChevronUp,
  ChevronDown,
  Search,
  Trash2,
} from 'lucide-react'

const SORT_FIELDS = [
  { value: 'score', label: 'Score' },
  { value: 'newest', label: 'Newest' },
  { value: 'effort', label: 'Effort' },
  { value: 'pursued', label: 'Pursued' },
]

export default function IdeaBoardAdminTab({ canWrite }) {
  const [selected, setSelected] = useState(null)
  const [sortField, setSortField] = useState('score')
  const [filterCat, setFilterCat] = useState('All')
  const [search, setSearch] = useState('')

  const { data: suggestions = [], isLoading } = useAdminSuggestions(true)
  const suggestionIds = useMemo(() => suggestions.map((s) => s.id), [suggestions])
  const { data: allAttachments = [] } = useIdeaBoardAttachments(suggestionIds)
  const attachmentsBySuggestion = useMemo(() => {
    const map = {}
    allAttachments.forEach((a) => {
      if (!map[a.suggestion_id]) map[a.suggestion_id] = []
      map[a.suggestion_id].push(a)
    })
    return map
  }, [allAttachments])
  const { data: profiles = [] } = useIdeaBoardProfiles(true)
  const { data: allVotes = [] } = useIdeaBoardVotes()
  const updateMutation = useAdminUpdateSuggestion()
  const deleteMutation = useAdminDeleteSuggestion()
  const categoryNames = useIdeaBoardCategoryNames()
  const categoryFilterOptions = useMemo(() => ['All', ...categoryNames], [categoryNames])

  const profileMap = useMemo(() => {
    const m = {}
    profiles.forEach((p) => {
      m[p.id] = p
    })
    return m
  }, [profiles])

  const scores = useMemo(() => {
    const map = {}
    allVotes.forEach((v) => {
      map[v.suggestion_id] = (map[v.suggestion_id] || 0) + v.value
    })
    return map
  }, [allVotes])

  const getVoteBreakdown = (suggestionId) => {
    const sVotes = allVotes.filter((v) => v.suggestion_id === suggestionId)
    return {
      up: sVotes.filter((v) => v.value === 1),
      down: sVotes.filter((v) => v.value === -1),
      total: sVotes.length,
    }
  }

  const getSubmitterName = (s) => {
    const profile = profileMap[s.created_by]
    if (profile) return ideaBoardDisplayName(profile, null)
    return s.submitter_name || 'Unknown'
  }

  const getVoterName = (userId) => {
    const profile = profileMap[userId]
    if (profile) return ideaBoardDisplayName(profile, null)
    return 'Unknown'
  }

  const sorted = useMemo(() => {
    let items =
      filterCat === 'All' ? suggestions : suggestions.filter((s) => s.category === filterCat)
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter((s) => s.title.toLowerCase().includes(q))
    }
    return [...items].sort((a, b) => {
      if (sortField === 'score') return (scores[b.id] || 0) - (scores[a.id] || 0)
      if (sortField === 'newest')
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      if (sortField === 'effort') {
        const order = { S: 1, M: 2, L: 3 }
        return (order[a.effort_estimate] || 99) - (order[b.effort_estimate] || 99)
      }
      if (sortField === 'pursued') return (b.is_pursued ? 1 : 0) - (a.is_pursued ? 1 : 0)
      return 0
    })
  }, [suggestions, filterCat, sortField, scores, search])

  const handleSave = async (id, patch) => {
    if (!canWrite) return
    await updateMutation.mutateAsync({ id, patch })
  }

  const handleToggleHidden = async (s, e) => {
    e?.stopPropagation()
    if (!canWrite) return
    await updateMutation.mutateAsync({ id: s.id, patch: { is_hidden: !s.is_hidden } })
  }

  const handleDelete = async (id, e) => {
    e?.stopPropagation()
    if (!canWrite) return
    try {
      await deleteMutation.mutateAsync(id)
      if (selected?.id === id) setSelected(null)
    } catch (err) {
      toast.error(err?.message || 'Failed to delete idea')
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <Shield className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Admin View
        </span>
        <span className="text-xs text-muted-foreground">
          — Review submissions, hide or delete ideas, see voters, set effort, and flag ideas as
          pursued.
        </span>
      </div>

      <CategoryAdminPanel canWrite={canWrite} />

      <div className="bg-white rounded-xl border border-border shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-44 h-8 text-sm">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categoryFilterOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortField} onValueChange={setSortField}>
          <SelectTrigger className="w-36 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_FIELDS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">{sorted.length} ideas</div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className="w-16 font-semibold text-xs text-muted-foreground">
                    Score
                  </TableHead>
                  <TableHead className="font-semibold text-xs text-muted-foreground">
                    Title
                  </TableHead>
                  <TableHead className="font-semibold text-xs text-muted-foreground">
                    Category
                  </TableHead>
                  <TableHead className="font-semibold text-xs text-muted-foreground">
                    Submitter
                  </TableHead>
                  <TableHead className="w-20 font-semibold text-xs text-muted-foreground">
                    Effort
                  </TableHead>
                  <TableHead className="w-24 font-semibold text-xs text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="w-20 font-semibold text-xs text-muted-foreground">
                    Date
                  </TableHead>
                  <TableHead className="w-32 font-semibold text-xs text-muted-foreground">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((s) => {
                  const score = scores[s.id] || 0
                  return (
                    <TableRow
                      key={s.id}
                      className="cursor-pointer hover:bg-secondary/30 transition-colors"
                      onClick={() => setSelected(s)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {score > 0 ? (
                            <ChevronUp className="w-3 h-3 text-emerald-500" />
                          ) : score < 0 ? (
                            <ChevronDown className="w-3 h-3 text-red-400" />
                          ) : null}
                          <span
                            className={`font-bold text-sm ${
                              score > 0
                                ? 'text-emerald-600'
                                : score < 0
                                  ? 'text-red-500'
                                  : 'text-muted-foreground'
                            }`}
                          >
                            {score}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-sm max-w-xs">
                        <span className="truncate block">{s.title}</span>
                      </TableCell>
                      <TableCell>
                        <CategoryBadge category={s.category} />
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          {s.is_anonymous && <EyeOff className="w-3 h-3" />}
                          {getSubmitterName(s)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {s.effort_estimate ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary text-secondary-foreground border border-border">
                            {s.effort_estimate}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.is_hidden ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                            Hidden
                          </span>
                        ) : s.is_pursued ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                            Pursued
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-muted-foreground">
                            Open
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(s.created_at), 'MMM d')}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setSelected(s)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors"
                            title="View details"
                          >
                            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                          {canWrite && (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={(e) => handleToggleHidden(s, e)}
                              >
                                {s.is_hidden ? 'Unhide' : 'Hide'}
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this idea?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This permanently removes the idea and all votes. This cannot
                                      be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={(e) => handleDelete(s.id, e)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-muted-foreground text-sm">
                      No ideas found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {selected && (
        <AdminDetailDialog
          suggestion={selected}
          breakdown={getVoteBreakdown(selected.id)}
          getVoterName={getVoterName}
          submitterName={getSubmitterName(selected)}
          attachments={attachmentsBySuggestion[selected.id] || []}
          onSave={handleSave}
          onToggleHidden={() => handleToggleHidden(selected)}
          onDelete={() => handleDelete(selected.id)}
          saving={updateMutation.isPending}
          deleting={deleteMutation.isPending}
          canWrite={canWrite}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

function AdminDetailDialog({
  suggestion,
  breakdown,
  getVoterName,
  submitterName,
  attachments = [],
  onSave,
  onToggleHidden,
  onDelete,
  saving,
  deleting,
  canWrite,
  onClose,
}) {
  const [isPursued, setIsPursued] = useState(suggestion.is_pursued || false)
  const [effort, setEffort] = useState(suggestion.effort_estimate || '')
  const [adminNotes, setAdminNotes] = useState(suggestion.admin_notes || '')

  const handleSaveAll = async () => {
    await onSave(suggestion.id, {
      is_pursued: isPursued,
      effort_estimate: effort || null,
      admin_notes: adminNotes,
    })
    onClose()
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-6 text-base font-semibold">{suggestion.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryBadge category={suggestion.category} />
            {suggestion.is_anonymous && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-border text-muted-foreground">
                <EyeOff className="w-3 h-3" />
                Anonymous
              </span>
            )}
            {suggestion.is_hidden && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                Hidden from board
              </span>
            )}
          </div>

          <div className="text-sm">
            <span className="text-muted-foreground">Submitted by: </span>
            <span className="font-medium">{submitterName}</span>
          </div>

          <div className="bg-secondary/50 rounded-lg p-4 text-sm leading-relaxed text-foreground">
            {suggestion.description}
          </div>

          {attachments.length > 0 && (
            <IdeaAttachmentPanel attachments={attachments} showDownload />
          )}

          <div className="rounded-lg border border-border p-4 bg-white">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2 mb-3">
              <Users className="w-3.5 h-3.5" />
              Vote Breakdown — {breakdown.total} total
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-2">
                  <ThumbsUp className="w-3 h-3" />
                  Upvotes ({breakdown.up.length})
                </div>
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {breakdown.up.length === 0 ? (
                    <p className="text-xs text-muted-foreground">None</p>
                  ) : (
                    breakdown.up.map((v) => (
                      <p key={v.id} className="text-xs text-muted-foreground">
                        {getVoterName(v.user_id)}
                      </p>
                    ))
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-red-600 mb-2">
                  <ThumbsDown className="w-3 h-3" />
                  Downvotes ({breakdown.down.length})
                </div>
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {breakdown.down.length === 0 ? (
                    <p className="text-xs text-muted-foreground">None</p>
                  ) : (
                    breakdown.down.map((v) => (
                      <p key={v.id} className="text-xs text-muted-foreground">
                        {getVoterName(v.user_id)}
                      </p>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {canWrite && (
            <div className="space-y-4 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Mark as Pursued</Label>
                  <p className="text-xs text-muted-foreground">
                    Flags this idea as being actioned
                  </p>
                </div>
                <Switch checked={isPursued} onCheckedChange={setIsPursued} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Effort Estimate</Label>
                <Select value={effort} onValueChange={setEffort}>
                  <SelectTrigger>
                    <SelectValue placeholder="Set effort level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="S">S — Small</SelectItem>
                    <SelectItem value="M">M — Medium</SelectItem>
                    <SelectItem value="L">L — Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">
                  Admin Notes{' '}
                  <span className="text-muted-foreground font-normal">(internal only)</span>
                </Label>
                <Textarea
                  placeholder="Internal notes, not visible to employees..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="min-h-[80px] resize-none"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onToggleHidden}
                  disabled={saving}
                >
                  {suggestion.is_hidden ? 'Unhide from board' : 'Hide from board'}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" disabled={deleting}>
                      Delete idea
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this idea?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the idea, all attachments, and all votes.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={onDelete}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <Button
                onClick={handleSaveAll}
                disabled={saving}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
